/**
 * Variant Indecision Tracker
 *
 * Detects when a shopper is indecisive about variant selection (color/size)
 * by tracking how many times they swap between variants within a time window.
 *
 * Fires when: 5+ variant swaps within 30 seconds, with oscillation (revisiting previous selections)
 */

import {
  SHOPPER_INTENT_EVENTS,
  type VariantIndecisionEventData,
} from "@redotech/redo-model/events/intent/shopper-intent";
import { fireIntentEvent, subscribeToNavigation } from "./types";

interface VariantChange {
  timestamp: number;
  variantId: string;
  optionType: "color" | "size" | "other";
}

const STORAGE_KEY = "redo-variant-indecision-state";
const VARIANT_SWAP_THRESHOLD = 5;
const VARIANT_SWAP_WINDOW_MS = 30_000;

interface VariantIndecisionState {
  variantChanges: VariantChange[];
  hasFired: boolean;
}

function loadState(): VariantIndecisionState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore storage errors
  }
  return { variantChanges: [], hasFired: false };
}

function saveState(state: VariantIndecisionState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

function pruneOldEntries(entries: VariantChange[]): VariantChange[] {
  const cutoff = Date.now() - VARIANT_SWAP_WINDOW_MS;
  return entries.filter((e) => e.timestamp > cutoff);
}

function detectOptionType(): "color" | "size" | "other" {
  const activeElement = document.activeElement;
  const label =
    activeElement
      ?.closest("[data-option-name]")
      ?.getAttribute("data-option-name") ??
    activeElement?.closest("fieldset")?.querySelector("legend")?.textContent ??
    "";

  const labelLower = label.toLowerCase();
  if (labelLower.includes("color") || labelLower.includes("colour")) {
    return "color";
  }
  if (labelLower.includes("size")) {
    return "size";
  }
  return "other";
}

export interface VariantIndecisionTracker {
  cleanup: () => void;
}

export function setupVariantIndecisionTracker(): VariantIndecisionTracker {
  const state = loadState();

  // Skip setup entirely if already fired
  if (state.hasFired) {
    return { cleanup: () => {} };
  }

  state.variantChanges = pruneOldEntries(state.variantChanges);
  saveState(state);

  let currentVariantId: string | null = null;
  const observers: MutationObserver[] = [];

  // Detect current variant from URL
  const urlParams = new URLSearchParams(window.location.search);
  const variantParam = urlParams.get("variant");
  if (variantParam) {
    currentVariantId = variantParam;
  }

  const checkAndFireEvent = (): void => {
    if (state.hasFired) {
      return;
    }

    const recentChanges = pruneOldEntries(state.variantChanges);

    if (recentChanges.length >= VARIANT_SWAP_THRESHOLD) {
      const uniqueVariants = new Set(recentChanges.map((c) => c.variantId));

      // If they've selected fewer unique variants than changes, they're oscillating
      if (uniqueVariants.size < recentChanges.length) {
        state.hasFired = true;
        saveState(state);

        // Count how many times each variant was selected
        const variantCounts = new Map<string, number>();
        for (const change of recentChanges) {
          variantCounts.set(
            change.variantId,
            (variantCounts.get(change.variantId) ?? 0) + 1,
          );
        }

        // Collect all option types that were changed
        const optionTypes = recentChanges.map((c) => c.optionType);

        const eventData: VariantIndecisionEventData = {
          swapCount: recentChanges.length,
          uniqueVariants: uniqueVariants.size,
          variants: Array.from(uniqueVariants),
          variantCounts: Object.fromEntries(variantCounts),
          windowMs: VARIANT_SWAP_WINDOW_MS,
          optionTypes,
        };

        fireIntentEvent({
          type: SHOPPER_INTENT_EVENTS.VARIANT_INDECISION,
          data: eventData,
        });
      }
    }
  };

  const recordVariantChange = (variantId: string): void => {
    const optionType = detectOptionType();

    state.variantChanges.push({ timestamp: Date.now(), variantId, optionType });

    state.variantChanges = pruneOldEntries(state.variantChanges);
    saveState(state);
    checkAndFireEvent();
  };

  const handleUrlChange = (): void => {
    const params = new URLSearchParams(window.location.search);
    const newVariantId = params.get("variant");

    if (newVariantId && newVariantId !== currentVariantId) {
      recordVariantChange(newVariantId);
      currentVariantId = newVariantId;
    }
  };

  // Subscribe to shared navigation listener
  const unsubscribeNavigation = subscribeToNavigation(handleUrlChange);

  // Observe DOM for variant selector changes
  const variantObserver = new MutationObserver(() => {
    setTimeout(handleUrlChange, 100);
  });

  const productForm = document.querySelector(
    'form[action*="/cart/add"], [data-product-form]',
  );
  if (productForm) {
    variantObserver.observe(productForm, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-selected-value", "checked", "aria-checked"],
    });
    observers.push(variantObserver);
  }

  return {
    cleanup: () => {
      unsubscribeNavigation();
      for (const o of observers) o.disconnect();
    },
  };
}
